// Wave 16 — Live-session usage summary for the /dashboard page.
//
// Shows the current tier's meter plainly so a rider isn't surprised
// mid-session. Hidden entirely on rapha.aero.chat and for Rapha-role
// users (they see no metering).

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useSubscription } from '../services/useSubscription';
import { isRaphaDomain } from '../services/subdomain';
import { readCurrentUser } from '../services/useCurrentUser';
import { colors } from '../theme';
import PaywallModal from './PaywallModal';

const STRIPE_ENABLED = process.env.REACT_APP_STRIPE_ENABLED === 'true';

function minutes(sec: number): number {
  return Math.round(sec / 60);
}

function formatResetLabel(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const daysLeft = Math.max(0, Math.ceil((d.getTime() - now) / 86_400_000));
    if (daysLeft === 0) return 'Resets today';
    if (daysLeft === 1) return 'Resets tomorrow';
    return `Resets in ${daysLeft} days`;
  } catch {
    return '';
  }
}

export default function UsageDashboardCard() {
  const { usage } = useSubscription();
  const [paywallOpen, setPaywallOpen] = useState(false);

  // Rapha bypass — subdomain OR role='rapha'.
  if (isRaphaDomain()) return null;
  const user = readCurrentUser();
  if (user.role === 'rapha') return null;
  if (!usage) return null;

  const isFree = usage.isFree;

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
          background: colors.gradSurface,
        }}
      >
        {isFree ? (
          <FreeContent
            weeklyUsedSec={usage.weeklyUsedSec}
            weeklyLimitSec={usage.weeklyLimitSec ?? 0}
            weeklyResetAt={usage.weeklyResetAt}
            onUpgradeClick={() => setPaywallOpen(true)}
          />
        ) : (
          <ProContent
            dailyUsedSec={usage.dailyUsedSec}
            dailyIncludedSec={usage.dailyIncludedSec ?? 0}
            todayOverageSec={usage.todayOverageSec}
            monthlyOverageSec={usage.monthlyOverageSec}
            monthlyOverageCents={usage.monthlyOverageCents}
            projectedMonthOverageCents={usage.projectedMonthOverageCents}
          />
        )}
      </Box>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason="upgrade_click"
      />
    </>
  );
}

function FreeContent(props: {
  weeklyUsedSec: number;
  weeklyLimitSec: number;
  weeklyResetAt: string;
  onUpgradeClick: () => void;
}) {
  const { weeklyUsedSec, weeklyLimitSec, weeklyResetAt, onUpgradeClick } = props;
  const usedMin = minutes(weeklyUsedSec);
  const limitMin = minutes(weeklyLimitSec) || 30;
  const pct = Math.min(100, Math.round((usedMin / limitMin) * 100));
  const critical = usedMin >= limitMin - 5;
  const barColor = critical ? colors.danger : colors.brand;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" fontWeight={800} sx={{ color: colors.text }}>
            {usedMin}
          </Typography>
          <Typography variant="h6" sx={{ color: colors.textMuted }}>
            / {limitMin} min this week
          </Typography>
        </Stack>
        <Chip
          label="Free"
          size="small"
          sx={{
            backgroundColor: `${colors.brand}22`,
            color: colors.brand,
            fontWeight: 600,
          }}
        />
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: colors.bgSubtle,
          '& .MuiLinearProgress-bar': { backgroundColor: barColor },
        }}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ color: colors.textMuted }}>
          {formatResetLabel(weeklyResetAt)} · Sunday 00:00 UTC
        </Typography>
        <Button
          onClick={onUpgradeClick}
          variant="contained"
          size="small"
          sx={{ backgroundColor: colors.brand }}
        >
          {STRIPE_ENABLED ? 'Upgrade to Pro' : 'Pro coming soon'}
        </Button>
      </Stack>
    </Stack>
  );
}

function ProContent(props: {
  dailyUsedSec: number;
  dailyIncludedSec: number;
  todayOverageSec: number;
  monthlyOverageSec: number;
  monthlyOverageCents: number;
  projectedMonthOverageCents: number;
}) {
  const {
    dailyUsedSec,
    dailyIncludedSec,
    todayOverageSec,
    monthlyOverageSec,
    monthlyOverageCents,
    projectedMonthOverageCents,
  } = props;
  const usedMin = minutes(dailyUsedSec);
  const includedMin = minutes(dailyIncludedSec) || 30;
  const overageTodayMin = minutes(todayOverageSec);
  const monthlyOverageMin = minutes(monthlyOverageSec);
  const inOverage = overageTodayMin > 0 || monthlyOverageMin > 0;

  if (!inOverage) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h4" fontWeight={800} sx={{ color: colors.text }}>
              {usedMin}
            </Typography>
            <Typography variant="h6" sx={{ color: colors.textMuted }}>
              / {includedMin} min today
            </Typography>
          </Stack>
          <Chip
            label="Pro"
            size="small"
            sx={{
              backgroundColor: `${colors.brand}22`,
              color: colors.brand,
              fontWeight: 600,
            }}
          />
        </Stack>
        <Typography variant="body2" sx={{ color: colors.textMuted }}>
          Included daily allowance · $19.99/mo base
        </Typography>
        <Chip
          label="No overage this month"
          size="small"
          sx={{
            alignSelf: 'flex-start',
            backgroundColor: `${colors.data}22`,
            color: colors.data,
          }}
        />
      </Stack>
    );
  }

  const monthlyDollars = (monthlyOverageCents / 100).toFixed(2);
  const projectedDollars = (projectedMonthOverageCents / 100).toFixed(2);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" fontWeight={800} sx={{ color: colors.warning }}>
            {overageTodayMin}
          </Typography>
          <Typography variant="h6" sx={{ color: colors.textMuted }}>
            min overage today
          </Typography>
        </Stack>
        <Chip
          label="Pro"
          size="small"
          sx={{
            backgroundColor: `${colors.brand}22`,
            color: colors.brand,
            fontWeight: 600,
          }}
        />
      </Stack>
      <Typography variant="body2" sx={{ color: colors.textMuted }}>
        This month so far: {monthlyOverageMin} min overage · ${monthlyDollars}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.textMuted }}>
        Projected month-end: ~${projectedDollars}
      </Typography>
      <Button
        href="/subscription"
        variant="outlined"
        size="small"
        sx={{
          alignSelf: 'flex-start',
          borderColor: colors.border,
          color: colors.text,
        }}
      >
        Manage billing
      </Button>
    </Stack>
  );
}
